/**
 * Two Sense — Dark hero background (video).
 *
 * The Services and Appointment pages share one hero treatment: a near-opaque
 * ink scrim over footage of the studio. Both pages used to inline the same
 * gradient + url() string, so a change had to be made twice; this is the single
 * definition.
 *
 * Rendered via <VideoBackground>, the same component the homepage Hero, About
 * and Appointment sections already use — muted/looped/inline autoplay, with the
 * poster shown until the video can play and `prefers-reduced-motion` honoured.
 *
 * ⚠️ THE VIDEO FILE IS NOT IN THE REPOSITORY.
 * `.gitignore` excludes `*.mp4` (line 44), so `public/videos/` ships posters
 * only. Until someone drops the file in, every hero on the site — this one, the
 * homepage Hero, About and Appointment — renders its poster and nothing else.
 * That is a graceful, silent fallback, which is exactly why it went unnoticed.
 * Deploy checklist: confirm `public/videos/hair-hero.mp4` exists before release.
 *
 * Poster fallback is a real photograph of the studio's hair floor (the
 * illuminated 2S mark is on the right-hand wall), derived from
 * `public/images/salon/hair-floor-interior.jpg` at two sizes:
 *   hero-bg-800.jpg   800 × 600   (~68 KB)  — xs/sm
 *   hero-bg.jpg      1600 × 1200  (~152 KB) — md and up
 *
 * Usage:
 *   const poster = useHeroPoster();
 *   <Box sx={{ ...heroContainerSx, pt: { xs: "8.5rem", md: "11rem" } }}>
 *       <VideoBackground src={HERO_VIDEO_SRC} poster={poster}
 *                        overlay={HERO_SCRIM} sx={{ zIndex: 0 }} />
 *       …content, each child positioned with its own zIndex…
 *   </Box>
 */

import useMediaQuery from "@mui/material/useMediaQuery";

/* Reuses the clip the homepage Hero already points at, so this introduces no
   new asset requirement. `public/videos/` also carries posters implying a
   wider intended set (beauty-floor, hair-floor, storefront) — switch this
   constant if a dedicated hair-floor clip is produced. */
export const HERO_VIDEO_SRC = "/videos/hair-hero.mp4";

export const HERO_POSTER_SM = "/images/hero-bg-800.jpg";
export const HERO_POSTER_LG = "/images/hero-bg.jpg";

/* Ink scrim. At 92–97% opacity the footage reads as texture and movement
   rather than as a picture — which is why a 240px poster went unnoticed here
   for so long. Keep the scrim this heavy: the white ceiling in the source
   footage is far too bright for legible reversed type without it. */
export const HERO_SCRIM =
    "linear-gradient(135deg, rgba(15,12,10,0.92) 0%, rgba(26,22,18,0.97) 100%)";

/* Ink ground beneath the video, so a hero still reads as intentional if BOTH
   the video and the poster fail to load. */
export const heroContainerSx = {
    position: "relative",
    overflow: "hidden",
    background: "#0a0a0a",
};

/**
 * Pick the poster size for the current viewport. `noSsr` forces immediate
 * evaluation so phones never briefly request the desktop file — this app is
 * client-rendered, so there is no hydration mismatch to worry about.
 */
export const useHeroPoster = () => {
    const isSmall = useMediaQuery("(max-width:899.95px)", { noSsr: true });
    return isSmall ? HERO_POSTER_SM : HERO_POSTER_LG;
};
